'use client';

import cn from 'classnames';
import { HTMLAttributes, ReactNode, useRef, useState } from 'react';
import { FolderOpenIcon } from '@phosphor-icons/react/dist/ssr';
import { Text } from '@/shared/ui/core';
import { MAX_FILES } from '@/shared/config/report-form';
import styles from './file-upload-box.module.scss';


type FileUploadBoxState = 'default' | 'dragover' | 'error';


export interface FileUploadBoxProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'title'> {
    title?: ReactNode;
    description?: ReactNode;
    hint?: ReactNode;
    error?: ReactNode;
    disabled?: boolean;
    state?: FileUploadBoxState;
    multiple?: boolean;
    accept?: string;
    onFilesSelect?: (files: FileList | null) => void;
}

const getSupportedFormatsHint = (accept?: string | undefined) => {
    if (!accept) return '';

    return accept.replace(/\./g, '*.');
}


export function FileUploadBox ({
    className,
    title = 'Select a file',
    description,
    hint,
    error,
    disabled = false,
    state = 'default',
    multiple = false,
    accept,
    onFilesSelect,
    ...props
}: FileUploadBoxProps) {
    const [ isDragOver, setIsDragOver ] = useState(false);
    const inputRef                      = useRef<HTMLInputElement>(null);

    const isError                          = Boolean(error) || state === 'error';
    const currentState: FileUploadBoxState = isError
        ? 'error'
        : isDragOver || state === 'dragover'
            ? 'dragover'
            : 'default';

    const handleFiles = (files: FileList | null) => {
        if (disabled) return;
        onFilesSelect?.(files);
    };

    const openFileDialog = () => {
        if (disabled) return;
        inputRef.current?.click();
    };

    const _hint = hint ?? `Supported file formats: ${ getSupportedFormatsHint(accept) } \n Up to ${ MAX_FILES } files`;

    return (
        <div className={ styles.root }>
            <div
                role="button"
                tabIndex={ disabled ? -1 : 0 }
                onClick={ openFileDialog }
                onKeyDown={ (event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openFileDialog();
                    }
                } }
                onDragEnter={ (event) => {
                    event.preventDefault();
                    if (!disabled) setIsDragOver(true);
                } }
                onDragOver={ (event) => {
                    event.preventDefault();
                    if (!disabled) setIsDragOver(true);
                } }
                onDragLeave={ (event) => {
                    event.preventDefault();
                    const relatedTarget = event.relatedTarget as Node | null;
                    if (!event.currentTarget.contains(relatedTarget)) {
                        setIsDragOver(false);
                    }
                } }
                onDrop={ (event) => {
                    event.preventDefault();
                    setIsDragOver(false);
                    if (disabled) return;
                    handleFiles(event.dataTransfer.files);
                } }
                className={ cn(
                    styles.dropzone,
                    disabled && styles.disabled,
                    currentState === 'dragover' && styles.dragover,
                    currentState === 'error' && styles.error,
                    className,
                ) }
                { ...props }
            >
                <input
                    ref={ inputRef }
                    type="file"
                    className={ styles.input }
                    multiple={ multiple }
                    accept={ accept }
                    disabled={ disabled }
                    onChange={ (event) => handleFiles(event.target.files) }
                />

                <div>
                    <Text
                        size='md'
                        tone="primary"
                        weight='medium'
                        lineHeight={ 1 }
                    >
                        Select a file:
                    </Text>

                    <FolderOpenIcon
                        className={ styles.icon }
                        size={ 24 }
                    />

                    <Text
                        tone="primary"
                        weight='medium'
                        lineHeight={ 1 }
                        className={ styles.title }
                    >
                        { title }
                    </Text>

                    {
                        description &&
                        <Text
                            tone="primary"
                            weight='medium'
                            lineHeight={ 1 }
                            className={ styles.title }
                        >
                            { description }
                        </Text>
                    }
                </div>
            </div>

            {
                !error &&
                _hint &&
                <Text
                    size='sm'
                    tone='secondary'
                    weight='regular'
                    lineHeight={ 1.1 }
                    className={ styles.message }
                >
                    { _hint }
                </Text>
            }
        </div>
    );
}
